import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchGa4Overview } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, Legend, LineChart, Line } from "recharts";
import { Users, Globe2, Smartphone, Repeat, Compass, TrendingUp, FileText, Calendar, Loader2, AlertCircle } from "lucide-react";

const PRESETS = [
  { key: "7d",   label: "Últimos 7 días",   start: -7 },
  { key: "30d",  label: "Últimos 30 días",  start: -30 },
  { key: "90d",  label: "Últimos 90 días",  start: -90 },
  { key: "ytd",  label: "Año actual",       start: "ytd" },
  { key: "1y",   label: "Últimos 12 meses", start: -365 },
];

const MONTH_NAMES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

/** Builds 12 month-options keyed "m-YYYY-MM" (most recent first). */
function buildMonthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const y = d.getFullYear();
    const m = d.getMonth(); // 0-11
    opts.push({
      key: `m-${y}-${String(m + 1).padStart(2, "0")}`,
      label: `${MONTH_NAMES[m]} ${y}`,
      year: y,
      month: m,
    });
  }
  return opts;
}
const MONTH_OPTIONS = buildMonthOptions();

const isoDate = (d) => d.toISOString().slice(0, 10);
function rangeFromPreset(key) {
  const end = new Date();
  if (key === "ytd") {
    const start = new Date(end.getFullYear(), 0, 1);
    return { start_date: isoDate(start), end_date: isoDate(end) };
  }
  // Month preset (m-YYYY-MM): full calendar month range
  if (typeof key === "string" && key.startsWith("m-")) {
    const opt = MONTH_OPTIONS.find((o) => o.key === key);
    if (opt) {
      const start = new Date(opt.year, opt.month, 1);
      const last = new Date(opt.year, opt.month + 1, 0); // last day of that month
      // If user picks the current month, cap end at today so we don't request future dates
      const today = new Date();
      const actualEnd = last > today ? today : last;
      return { start_date: isoDate(start), end_date: isoDate(actualEnd) };
    }
  }
  const preset = PRESETS.find((p) => p.key === key) || PRESETS[1];
  const start = new Date();
  start.setDate(start.getDate() + preset.start);
  return { start_date: isoDate(start), end_date: isoDate(end) };
}

const COLORS = ["#6366f1", "#10b981", "#f59e0b", "#f43f5e", "#0ea5e9", "#8b5cf6", "#ec4899", "#14b8a6"];

const fmtNum = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return v.toLocaleString("es-AR", { maximumFractionDigits: 0 });
};
const fmtDuration = (s) => {
  const sec = Number(s || 0);
  const m = Math.floor(sec / 60);
  const r = Math.floor(sec % 60);
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
};
const pct = (x) => `${(Number(x || 0) * 100).toFixed(1)}%`;

export default function AnalyticsSection({ propertyId: initialPid }) {
  const [propertyId, setPropertyId] = useState(initialPid || "");
  const [preset, setPreset] = useState("30d");
  const [body, setBody] = useState(null);
  const [editing, setEditing] = useState(!initialPid);

  // Custom range state (only used when preset === "custom")
  const todayIso = isoDate(new Date());
  const monthAgoIso = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return isoDate(d);
  })();
  const [customFrom, setCustomFrom] = useState(monthAgoIso);
  const [customTo, setCustomTo] = useState(todayIso);

  const handleRun = () => {
    if (!propertyId) return;
    const range = preset === "custom"
      ? { start_date: customFrom, end_date: customTo }
      : rangeFromPreset(preset);
    setBody({ property_id: propertyId.trim(), ...range });
  };

  const applyCustomRange = () => {
    if (!customFrom || !customTo || !propertyId) return;
    setBody({ property_id: propertyId.trim(), start_date: customFrom, end_date: customTo });
  };

  // Auto-load on mount when we already have a configured property
  useEffect(() => {
    if (initialPid && !body) {
      const range = rangeFromPreset(preset);
      setBody({ property_id: initialPid.trim(), ...range });
    }
    // We deliberately only trigger this on mount or when the configured pid changes,
    // never on `body` changes (would create an infinite loop with the setBody below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPid]);

  // Re-fetch when period changes (only if we already loaded once).
  // We exclude `body` from deps on purpose: this effect WRITES to body, including
  // it would create an infinite render loop. For "custom" we wait for the explicit Apply click.
  useEffect(() => {
    if (body && propertyId && preset !== "custom") {
      const range = rangeFromPreset(preset);
      setBody({ property_id: propertyId.trim(), ...range });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["ga4-overview", body],
    queryFn: () => fetchGa4Overview(body),
    enabled: !!body,
    staleTime: 5 * 60 * 1000,
  });

  const hasConfiguredPid = !!initialPid && !editing;

  const periodSelect = (
    <Select value={preset} onValueChange={setPreset}>
      <SelectTrigger className="h-9 text-sm w-56" data-testid="ga4-period">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="max-h-[400px]">
        <div className="px-2 py-1 text-[9px] font-bold uppercase tracking-widest text-slate-400">Atajos</div>
        {PRESETS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
        <div className="px-2 pt-2 py-1 text-[9px] font-bold uppercase tracking-widest text-slate-400">Por mes</div>
        {MONTH_OPTIONS.map((o) => <SelectItem key={o.key} value={o.key} data-testid={`ga4-period-${o.key}`}>{o.label}</SelectItem>)}
        <div className="border-t border-slate-100 mt-1" />
        <SelectItem value="custom" data-testid="ga4-period-custom">Rango personalizado…</SelectItem>
      </SelectContent>
    </Select>
  );

  const customRangeRow = preset === "custom" && (
    <div className="flex flex-wrap items-end gap-2 mt-2 pl-1 border-l-2 border-indigo-300" data-testid="ga4-custom-range-row">
      <div>
        <label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold block">Desde</label>
        <Input
          type="date"
          value={customFrom}
          onChange={(e) => setCustomFrom(e.target.value)}
          max={customTo || todayIso}
          className="h-9 text-xs w-40"
          data-testid="ga4-custom-from"
        />
      </div>
      <div>
        <label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold block">Hasta</label>
        <Input
          type="date"
          value={customTo}
          onChange={(e) => setCustomTo(e.target.value)}
          min={customFrom}
          max={todayIso}
          className="h-9 text-xs w-40"
          data-testid="ga4-custom-to"
        />
      </div>
      <Button
        size="sm"
        onClick={applyCustomRange}
        disabled={!customFrom || !customTo || !propertyId}
        className="h-9"
        data-testid="ga4-custom-apply"
      >
        Aplicar
      </Button>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="analytics-section">
      <div className="bg-white border border-slate-200 rounded-md p-4">
        {hasConfiguredPid ? (
          <>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 text-xs">
                <FileText className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-slate-500">GA4 Property:</span>
                <span className="font-mono font-semibold text-slate-900">{propertyId}</span>
                <button onClick={() => setEditing(true)} className="text-indigo-600 hover:underline ml-1">cambiar</button>
              </div>
              <div className="flex items-center gap-2">
                {periodSelect}
                {isLoading && <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />}
              </div>
            </div>
            {customRangeRow}
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_220px_120px] gap-3 items-end">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
                  <FileText className="h-3 w-3" /> GA4 Property ID
                </label>
                <Input
                  value={propertyId}
                  onChange={(e) => setPropertyId(e.target.value)}
                  placeholder="ej. 123456789"
                  className="h-9 text-sm font-mono"
                  data-testid="ga4-property-id"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
                  <Calendar className="h-3 w-3" /> Período
                </label>
                {periodSelect}
              </div>
              <Button onClick={handleRun} disabled={!propertyId || isLoading} className="h-9" data-testid="ga4-run-btn">
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Cargar"}
              </Button>
            </div>
            {customRangeRow}
            <p className="text-[11px] text-slate-500 mt-2">
              {initialPid ? "Ingresá otro ID o " : "Para que se cargue automáticamente, guardalo en "}
              <span className="font-medium">Configurar compromiso → GA4 Property ID</span>. El email <code className="font-mono bg-slate-100 px-1 rounded">narwhal-dashboard@dashboards-emergent.iam.gserviceaccount.com</code> debe estar como <span className="font-medium">Viewer</span> en la property GA4.
            </p>
          </>
        )}
      </div>

      {!body && !initialPid && (
        <div className="bg-slate-50 border border-slate-200 rounded-md p-8 text-center text-sm text-slate-500" data-testid="ga4-empty">
          Ingresá el Property ID de GA4 y apretá <span className="font-semibold">Cargar</span> para ver la data.
        </div>
      )}

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-md p-4 text-sm text-rose-800 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">No se pudo cargar la data</div>
            <div className="text-xs mt-1">{error?.response?.data?.detail || error?.message}</div>
          </div>
        </div>
      )}

      {data && (
        <>
          <SummaryCards summary={data.summary} byMonth={data.by_month} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ChannelsCard report={data.acquisition?.channels} />
            <SourcesCard report={data.acquisition?.sources} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <DevicesCard report={data.audience?.devices} />
            <NewVsReturningCard report={data.audience?.new_vs_returning} />
            <GeoCard report={data.audience?.geo?.by_country} />
          </div>
          <PagesCard report={data.behavior?.pages} />
          <MonthlyChart report={data.by_month} />
        </>
      )}
    </div>
  );
}

function SummaryCards({ summary, byMonth }) {
  const m = (summary?.rows?.[0]?.metrics) || [];
  const headers = summary?.metric_headers || [];
  const idx = (name) => headers.indexOf(name);
  const get = (name) => parseFloat(m[idx(name)] || 0);
  const cards = [
    { label: "Usuarios", value: fmtNum(get("activeUsers")), tone: "text-indigo-600", Icon: Users },
    { label: "Sesiones", value: fmtNum(get("sessions")), tone: "text-emerald-600", Icon: TrendingUp },
    { label: "Páginas vistas", value: fmtNum(get("screenPageViews")), tone: "text-amber-600", Icon: FileText },
    { label: "Duración promedio", value: fmtDuration(get("averageSessionDuration")), tone: "text-sky-600", Icon: Calendar },
    { label: "Bounce Rate", value: pct(get("bounceRate")), tone: "text-rose-600", Icon: AlertCircle },
    { label: "Engagement Rate", value: pct(get("engagementRate")), tone: "text-violet-600", Icon: Compass },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      {cards.map((c, i) => (
        <div key={i} className="bg-white border border-slate-200 rounded-md p-3" data-testid={`ga4-kpi-${c.label}`}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{c.label}</span>
            <c.Icon className={`h-3.5 w-3.5 ${c.tone}`} />
          </div>
          <div className="font-display text-xl font-bold text-slate-900">{c.value}</div>
        </div>
      ))}
    </div>
  );
}

function TableCard({ title, icon: Icon, headers, rows, dimIdx = 0 }) {
  if (!rows || rows.length === 0) return (
    <div className="bg-white border border-slate-200 rounded-md p-5 text-center text-sm text-slate-400">Sin data</div>
  );
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <Icon className="h-4 w-4 text-indigo-600" />
        <h3 className="font-display text-sm font-semibold text-slate-900">{title}</h3>
      </div>
      <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
        {rows.slice(0, 10).map((r, i) => (
          <div key={i} className="px-4 py-2 flex items-center justify-between text-xs">
            <span className="text-slate-800 truncate" title={r.dims.join(" · ")}>{r.dims[dimIdx] || "(sin definir)"}</span>
            <div className="flex gap-3 font-mono text-slate-700 shrink-0">
              {headers.slice(1).map((_, j) => (<span key={j}>{fmtNum(r.metrics[j + 1] || r.metrics[0])}</span>))}
              <span className="font-semibold text-indigo-600">{fmtNum(r.metrics[0])}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChannelsCard({ report }) {
  const rows = report?.rows || [];
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <Compass className="h-4 w-4 text-emerald-600" />
        <h3 className="font-display text-sm font-semibold text-slate-900">Canales de adquisición</h3>
      </div>
      <div className="p-3">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={rows.slice(0, 8).map((r) => ({ name: r.dims[0], sessions: parseInt(r.metrics[0]), conv: parseFloat(r.metrics[3] || 0) }))}>
            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Bar dataKey="sessions" fill="#10b981" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function SourcesCard({ report }) {
  const rows = report?.rows || [];
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <Globe2 className="h-4 w-4 text-sky-600" />
        <h3 className="font-display text-sm font-semibold text-slate-900">Fuentes / Medio</h3>
      </div>
      <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
        {rows.slice(0, 10).map((r, i) => (
          <div key={i} className="px-4 py-2 flex items-center justify-between text-xs">
            <span className="text-slate-800 truncate">{r.dims[0]} <span className="text-slate-400">/ {r.dims[1]}</span></span>
            <span className="font-mono font-semibold text-sky-600">{fmtNum(r.metrics[0])}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DevicesCard({ report }) {
  const rows = report?.rows || [];
  const data = rows.map((r) => ({ name: r.dims[0], value: parseInt(r.metrics[0]) }));
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <Smartphone className="h-4 w-4 text-amber-600" />
        <h3 className="font-display text-sm font-semibold text-slate-900">Dispositivos</h3>
      </div>
      <div className="p-3">
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" outerRadius={70} label={(d) => d.name}>
              {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v) => fmtNum(v)} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function NewVsReturningCard({ report }) {
  const rows = report?.rows || [];
  const data = rows.map((r) => ({ name: r.dims[0] || "—", value: parseInt(r.metrics[0]) }));
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <Repeat className="h-4 w-4 text-violet-600" />
        <h3 className="font-display text-sm font-semibold text-slate-900">Nuevos vs recurrentes</h3>
      </div>
      <div className="p-3">
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" outerRadius={70} label={(d) => d.name}>
              {data.map((_, i) => <Cell key={i} fill={COLORS[(i + 4) % COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v) => fmtNum(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function GeoCard({ report }) {
  const rows = report?.rows || [];
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <Globe2 className="h-4 w-4 text-indigo-600" />
        <h3 className="font-display text-sm font-semibold text-slate-900">Países</h3>
      </div>
      <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
        {rows.slice(0, 12).map((r, i) => (
          <div key={i} className="px-4 py-2 flex items-center justify-between text-xs">
            <span className="text-slate-800 truncate">{r.dims[0]}</span>
            <span className="font-mono font-semibold text-indigo-600">{fmtNum(r.metrics[0])}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PagesCard({ report }) {
  const rows = report?.rows || [];
  // Detect the dominant hostname (by total views) so we can flag foreign hosts.
  const totals = new Map();
  for (const r of rows) {
    const host = r.dims?.[0] || "";
    const views = parseInt(r.metrics?.[0] || 0, 10);
    totals.set(host, (totals.get(host) || 0) + views);
  }
  const dominantHost = [...totals.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || "";
  const hasForeign = totals.size > 1;

  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
        <FileText className="h-4 w-4 text-rose-600" />
        <h3 className="font-display text-sm font-semibold text-slate-900">Páginas más visitadas</h3>
        {dominantHost && (
          <span className="text-[10px] font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{dominantHost}</span>
        )}
        {hasForeign && (
          <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded ml-auto" data-testid="ga4-foreign-host-warning">
            ⚠ Tráfico de {totals.size} dominios distintos
          </span>
        )}
      </div>
      <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
        <div className="px-4 py-2 grid grid-cols-[1fr_80px_80px_70px] gap-3 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold sticky top-0">
          <div>Página</div>
          <div className="text-right">Vistas</div>
          <div className="text-right">Duración</div>
          <div className="text-right">Rebote</div>
        </div>
        {rows.map((r, i) => {
          const host = r.dims?.[0] || "";
          const path = r.dims?.[1] || "";
          const title = r.dims?.[2] || "";
          const foreign = dominantHost && host && host !== dominantHost;
          return (
            <div key={i} className={`px-4 py-2 grid grid-cols-[1fr_80px_80px_70px] gap-3 items-center text-xs ${foreign ? "bg-rose-50/60" : ""}`} title={foreign ? `Dominio distinto al principal (${dominantHost}). Revisá el tag de GA4.` : undefined}>
              <div className="min-w-0">
                <div className="text-slate-800 truncate" title={title}>{title || path}</div>
                <div className="text-[10px] text-slate-400 truncate font-mono flex items-center gap-1">
                  {foreign && <span className="text-rose-600 font-semibold not-italic" title="Dominio distinto al principal">⚠</span>}
                  <span className={foreign ? "text-rose-700" : ""}>{host || "(sin host)"}</span>
                  <span className="text-slate-300">·</span>
                  <span>{path}</span>
                </div>
              </div>
              <div className="text-right font-mono font-semibold text-rose-600">{fmtNum(r.metrics[0])}</div>
              <div className="text-right font-mono text-slate-600">{fmtDuration(r.metrics[2])}</div>
              <div className="text-right font-mono text-slate-600">{pct(r.metrics[3])}</div>
            </div>
          );
        })}
      </div>
      {hasForeign && (
        <div className="px-4 py-2 bg-amber-50 border-t border-amber-200 text-[10px] text-amber-900 leading-relaxed" data-testid="ga4-foreign-host-banner">
          <strong>Cross-contamination detectado:</strong> filas en rosa son páginas de dominios distintos al principal. Verificá que el measurement ID de GA4 de este cliente esté instalado solo en sus dominios. Si <span className="font-mono">{[...totals.keys()].filter((h) => h !== dominantHost).join(", ")}</span> no corresponde, sacale el tag.
        </div>
      )}
    </div>
  );
}

function MonthlyChart({ report }) {
  const rows = report?.rows || [];
  if (rows.length < 2) return null;
  const data = rows.map((r) => ({
    month: r.dims[0],
    sessions: parseInt(r.metrics[0]),
    users: parseInt(r.metrics[1]),
    pageviews: parseInt(r.metrics[2]),
  })).sort((a, b) => a.month.localeCompare(b.month));
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-indigo-600" />
        <h3 className="font-display text-sm font-semibold text-slate-900">Evolución mensual</h3>
      </div>
      <div className="p-3">
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data}>
            <XAxis dataKey="month" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="sessions" stroke="#6366f1" strokeWidth={2} dot={false} name="Sesiones" />
            <Line type="monotone" dataKey="users" stroke="#10b981" strokeWidth={2} dot={false} name="Usuarios" />
            <Line type="monotone" dataKey="pageviews" stroke="#f59e0b" strokeWidth={2} dot={false} name="Páginas vistas" />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
